import { Bot, Check, FileText, Import, Notebook, PenLine, Unlock, X } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { SectionHeading, SETTLE_SPRING } from "./SectionHeading";

/**
 * The Notebook as a differentiator: the agent doesn't just do the work, it
 * writes it down — in a plain-markdown notebook you own.
 * Left column carries the pitch; the right card shows a real agent-written
 * note with a keep/discard suggestion, so "nothing changes without your ok"
 * is visible, not just claimed.
 */
export function SecondBrainSection() {
  const { t } = useTranslation("home");

  const points = [
    { icon: FileText, text: t("secondBrain.pointOne") },
    { icon: PenLine, text: t("secondBrain.pointTwo") },
    { icon: Import, text: t("secondBrain.pointThree") },
  ];

  const chips = [
    { icon: FileText, label: t("secondBrain.chipMarkdown") },
    { icon: Import, label: t("secondBrain.chipImport") },
    { icon: Unlock, label: t("secondBrain.chipLockin") },
  ];

  return (
    <section className="bg-home-cream-deep px-5 py-20 md:py-24">
      <div className="mx-auto grid max-w-6xl items-center gap-12 md:grid-cols-2">
        {/* Left — the pitch */}
        <div>
          <SectionHeading align="left" kicker={t("secondBrain.kicker")} title={t("secondBrain.title")} />

          <p className="mt-6 max-w-md font-body text-[16px] leading-relaxed text-home-ink/75">
            {t("secondBrain.lede")}
          </p>

          <ul className="mt-8 space-y-4">
            {points.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-home-terracotta/12 text-home-terracotta">
                  <Icon className="h-4 w-4" strokeWidth={2.2} />
                </span>
                <span className="font-body text-[15px] leading-snug text-home-ink/85">{text}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-wrap items-center gap-2">
            {chips.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 rounded-full border border-home-ink/12 px-3 py-1.5 font-body text-[12px] font-semibold text-home-ink/65"
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* Right — a real notebook page */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={SETTLE_SPRING}
          className="overflow-hidden rounded-[26px] bg-white shadow-[0_30px_66px_-32px_rgba(33,31,28,0.5)]"
        >
          <div className="flex items-center gap-2.5 border-b border-home-ink/8 px-5 py-3.5">
            <Notebook className="h-[18px] w-[18px] text-home-terracotta" strokeWidth={2} />
            <span className="font-display text-[15px] font-semibold text-home-ink">
              {t("secondBrain.cardTitle")}
            </span>
            <span className="ml-auto font-body text-[11px] text-home-ink/65">
              {t("secondBrain.cardTime")}
            </span>
          </div>

          <div className="px-5 py-5">
            <p className="font-display text-[17px] font-semibold text-home-ink">
              {t("secondBrain.noteTitle")}
            </p>
            <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-home-terracotta/12 px-2.5 py-1 font-body text-[11px] font-bold text-home-oxblood">
              <Bot className="h-3 w-3" strokeWidth={2.4} />
              {t("secondBrain.byAgent")}
            </span>

            <p className="mt-3 font-body text-[14px] leading-relaxed text-home-ink/70">
              {t("secondBrain.noteLead")}
              <mark className="rounded bg-home-yellow/55 px-0.5 text-home-ink">
                {t("secondBrain.noteHighlight")}
              </mark>
            </p>

            {/* Pending agent contribution — keep/discard makes the control visible */}
            <div className="mt-4 rounded-2xl border border-home-terracotta/25 bg-home-terracotta/[0.05] p-4">
              <div className="flex items-center gap-1.5 font-body text-[11px] font-bold text-home-oxblood">
                <Bot className="h-3.5 w-3.5" strokeWidth={2.4} />
                {t("secondBrain.suggestLabel")}
              </div>
              <p className="mt-2 font-body text-[13.5px] leading-snug text-home-ink/85">
                {t("secondBrain.suggestBody")}
              </p>
              <div className="mt-3 flex items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-home-terracotta px-3.5 py-1.5 font-body text-[12px] font-bold text-white">
                  <Check className="h-3.5 w-3.5" strokeWidth={2.6} />
                  {t("secondBrain.keep")}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full border border-home-ink/15 px-3.5 py-1.5 font-body text-[12px] font-bold text-home-ink/65">
                  <X className="h-3.5 w-3.5" strokeWidth={2.6} />
                  {t("secondBrain.discard")}
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
